import { useMaterialScopeOptions } from '../../hooks/useMaterialScopeOptions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { useConsumptions } from '../../../hooks/useConsumptions';
import { useWarehouses } from '../../../hooks/useWarehouses';
import { ITEMS_PER_PAGE, INITIAL_FILTERS } from '../constants/consumptionsConstants';
import { mapConsumptionToDisplay } from '../utils/consumptionsUtils';

const useDebouncedValue = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

export const useConsumptionsTable = () => {
  const scopeOptions = useMaterialScopeOptions();
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConsumptionId, setSelectedConsumptionId] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const debouncedOrderNumber = useDebouncedValue(filters.order_number);
  const tableSort = useTableSort(() => setPage(1));
  const { sort } = tableSort;

  const queryFilters = useMemo(
    () => ({
      ...filters,
      order_number: debouncedOrderNumber,
      page,
      per_page: rowsPerPage,
      search: searchTerm,
      ...(sort ? { sort_by: sort.key, sort_dir: sort.direction } : {}),
    }),
    [filters, debouncedOrderNumber, page, rowsPerPage, searchTerm, sort]
  );

  const {
    data: consumptionsResponse,
    isLoading,
    refetch,
  } = useConsumptions(queryFilters);

  const { data: warehouses = [] } = useWarehouses({ enabled: true });

  const consumptions = useMemo(() => {
    if (Array.isArray(consumptionsResponse)) return consumptionsResponse;
    return consumptionsResponse?.results || [];
  }, [consumptionsResponse]);

  const displayConsumptions = useMemo(
    () => consumptions.map((consumption) => mapConsumptionToDisplay(consumption)),
    [consumptions]
  );

  const data = displayConsumptions;
  const totalCount = Array.isArray(consumptionsResponse)
    ? data.length
    : consumptionsResponse?.total || 0;
  const totalPages = Math.max(
    1,
    Array.isArray(consumptionsResponse)
      ? Math.ceil(totalCount / rowsPerPage)
      : consumptionsResponse?.pages || 1
  );

  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  }, []);

  const handleViewDetail = (consumption) => {
    setSelectedConsumptionId(consumption.uuid);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedConsumptionId(null);
  };

  const handleRefreshList = async () => {
    await refetch();
  };

  return {
    scopeOptions,
    data,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    filters,
    warehouses,
    detailDialogOpen,
    selectedConsumptionId,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleViewDetail,
    handleCloseDetailDialog,
    handleRefreshList,
    sort,
    handleSort: tableSort.toggleSort,
    resetSort: tableSort.resetSort,
  };
};
